import { Component, DoCheck, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AddItemsFromList } from '../../dialogs/add-items-from-list/add-items-from-list.component';
import { AddNewProfile } from '../../dialogs/add-new-profile/add-new-profile.component';
import { ProfileService } from '@services/profiles/profile.service';
import { GroupService } from '@services/groups/group.service';
import {
  GroupHasRoleInOrganisationService
} from '@services/groups/group-has-role-in-organisation.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { GroupHasSecuredAppService } from '@services/groups/group-has-secured-app.service';
import { PackageService } from '@services/packages/package.service';
import { GroupHasProfileService } from '@services/groups/group-has-profile.service';
import { CredentialsService } from '@services/credentials/credentials.service';
import { OrganisationService } from '@services/organisations/organisation.service';
import { TranslateService } from '@services/translate/translate.service';
import { GroupHasContentService } from '@services/groups/group-has-content.service';
import { DialogService } from '@services/dialog/dialog.service';

@Component({
    selector: 'group-edit-form',
    templateUrl: './group-edit-form.component.html',
    styleUrls: ['./group-edit-form.component.scss'],
    providers: [
        ProfileService, GroupService, GroupHasRoleInOrganisationService, PackageInstalledAppsService, GroupHasSecuredAppService,
        PackageService, GroupHasProfileService, CredentialsService, OrganisationService
    ],
    standalone: false
})
export class GroupEditFormComponent implements OnInit, DoCheck {
  @Input() organisationId;
  @Input() itemId;

  @Output() changed: EventEmitter<boolean> = new EventEmitter();
  hasChanged = false;

  group: Models.Group;
  profile: Models.Profile;
  displayAssignedContent = false;


  contentManager = false;
  contentAuthor = false;
  userManager = false;
  orderAdmin = false;
  ticketingAdmin = false;
  chatAdmin = false;
  securedContentAdmin = false;
  backendUserAdmin = false;
  assignedContentData: any;
  groupData: Models.GroupHasProfile[] = [];

  missingTitle = false;
  missingPermissions = false;

  title: string;
  ghrio: Models.GroupHasRoleInOrganisation[];
  parentGroupData: Models.GroupHasProfile[] = [];
  childGroupData: Models.GroupHasProfile[] = [];
  ghp: Models.GroupHasProfile[];
  ghc: any[];
  packages: Models.Package[];
  packageInstalledApps: Models.PackageInstalledApp[];
  packageInstalledAppsSecured: Models.PackageInstalledApp[];
  packageInstalledAppsDynamicContent: Models.PackageInstalledApp[];
  existingAssignedItemsConnections: Models.GroupHasContent[];
  assignedContentColumns = [{ prop: 'title' }, { prop: 'dynamicContentEntrance_Id' }];
  profileColumns = [{ prop: 'email' }, { prop: 'firstName' }, { prop: 'lastName' }];
  headerButtonsContent = [{
    icon: 'add', callback: () => {
      return this.addAssignedItem()
    }
  }];
  headerButtonsProfileGroup = [
    {
      icon: 'person_add', callback: () => {
        return this.addNewProfile()
      }, tooltipTranslation: 'userAdmin.group.addNewProfile'
    },
    {
      icon: 'add', callback: () => {
        return this.addProfile()
      }, tooltipTranslation: 'userAdmin.group.addProfile'
    }
  ];

  history: any;
  systemMessages: any;

  constructor(
    private route: ActivatedRoute, private profileService: ProfileService, private groupService: GroupService, private translate: TranslateService, private dialog: MatDialog,
    private ghrioService: GroupHasRoleInOrganisationService, private router: Router, private ghsaService: GroupHasSecuredAppService, private snackbar: SnackbarService,
    private appService: PackageInstalledAppsService, private ghcService: GroupHasContentService, private ghpService: GroupHasProfileService, private packageService: PackageService,
    private dialogService: DialogService
  ) {
  }

  @HostListener('window:beforeunload')
  @HostListener('window:beforeunload', ['$event'])
  canDeactivate(): Observable<boolean> | boolean | Promise<boolean> {
    return this.handleBeforeUnload();
  }

  async ngDoCheck() {
    if (this.hasChanged !== !this.handleBeforeUnload()) {
      this.hasChanged = !this.handleBeforeUnload();
      this.changed.emit(this.hasChanged);
    }
  }

  async ngOnInit() {
    this.title = this.translate.get('userAdmin.group.new');
    this.systemMessages = this.translate.get('system');
    this.group = new Models.Group();
    this.group.isPersonal = false;
    if (this.itemId) {
      this.group = await this.groupService.get(this.itemId);
      if ((this.group?.name || '').startsWith('pref_')) {
        this.group.name = this.group.name.split('_').splice(2, this.group.name.split('_').length - 1).join('_');
      }
      this.ghrio = (await this.ghrioService.find(0, 1000, { GroupID: this.itemId })).data;

      const roleMapping = {
        '36': 'contentManager', '42': ['contentAuthor', 'displayAssignedContent'], '43': 'userManager', '47': 'chatAdmin', '50': 'orderAdmin', '54': 'ticketingAdmin',
        '56': 'securedContentAdmin', '58': 'backendUserAdmin'
      };

      Object.keys(roleMapping).forEach(roleID => {
        const properties = roleMapping[roleID];
        if (Array.isArray(properties)) {
          properties.forEach(prop => this[prop] = !!this.ghrio.find(el => el.RoleID.toString() === roleID));
        } else {
          this[properties] = !!this.ghrio.find(el => el.RoleID.toString() === roleID);
        }
      });
      this.packages = (await this.packageService.find(0, 1000, { OwnerOrganisationID: this.organisationId })).data;
      this.packageInstalledApps = (await this.appService.find(0, 1000, { packageId: { $in: this.packages.map(el => el._id) } })).data;
      this.packageInstalledAppsSecured = this.packageInstalledApps.filter(el => !!el.isSecured);
      this.packageInstalledAppsDynamicContent = this.packageInstalledApps.filter(el => !!el.dynamicContentEntranceId);
      await this.initGroups();
    } else {
      this.packages = (await this.packageService.find(0, 1000, { OwnerOrganisationID: this.organisationId })).data;
      this.packageInstalledApps = (await this.appService.find(0, 1000, { packageId: { $in: this.packages.map(el => el._id) } })).data;
      this.packageInstalledAppsSecured = this.packageInstalledApps.filter(el => !!el.isSecured);
      this.packageInstalledAppsDynamicContent = this.packageInstalledApps.filter(el => !!el.dynamicContentEntranceId);
      this.assignedContentData = [];
    }

    this.initHistory();
  }

  initHistory(): void {
    this.history = {};
    ['group', 'contentManager', 'contentAuthor', 'userManager', 'orderAdmin', 'ticketingAdmin', 'backendUserAdmin', 'chatAdmin', 'securedContentAdmin', 'groupData',
      'assignedContentData', 'childGroupData', 'parentGroupData'
    ].forEach(field => {
      this.history[field] = JSON.stringify(this[field])
    });
  }

  handleBeforeUnload() {
    return !Object.keys(this?.history || {}).some(key => this.history?.[key] !== JSON.stringify(this?.[key]));
  }

  async initGroups() {
    this.title = this.translate.get('userAdmin.group.title') + ': ' + this.group.name;
    this.existingAssignedItemsConnections = (await this.ghcService.find(0, 1000, { Groups_GroupID: this.itemId })).data;
    const assignedApps = [];
    this.existingAssignedItemsConnections.map(el => {
      const assignedApp = this.packageInstalledApps.find(app => app.dynamicContentEntranceId === el.dynamicContentEntrance_Id);
      let title = '';
      if (assignedApp && assignedApp.translations && assignedApp.translations[0] && assignedApp.translations[0].appTitle) {
        title = assignedApp.translations[0].appTitle;
      }
      assignedApps.push({ dynamicContentEntrance_Id: el.dynamicContentEntrance_Id, title });
    });
    this.assignedContentData = assignedApps;
    this.ghc = JSON.parse(JSON.stringify(this.assignedContentData));
    this.groupData = (await this.ghpService.find(0, 1000, { GroupID: this.itemId })).data;
    this.ghp = JSON.parse(JSON.stringify(this.groupData));
  }

  async save() {
    this.missingTitle = !this.group.name || this.group.name.length < 3;
    this.missingPermissions = (!this.contentAuthor && !this.contentManager && !this.userManager);
    if (this.missingTitle || this.missingPermissions) {
      return;
    }
    let res;
    try {
      const group = Object.assign({}, this.group);
      group.name = 'pref_' + this.organisationId + '_' + group.name;
      res = !!this.itemId ? await this.groupService.update(this.itemId, group) : await this.groupService.create(group);
    } catch (err) {
      return this.snackbar.open('Die Gruppe konnte nicht erstellt werden, möglicherweise existiert bereits eine Gruppe mit dem gleichen Namen, versuchen Sie es mit einem anderen Namen.', 10000);
    }
    await this.toggleRole(res.GroupID, this.orderAdmin, 50);
    await this.toggleRole(res.GroupID, this.securedContentAdmin, 56);
    await this.toggleRole(res.GroupID, this.ticketingAdmin, 54);
    await this.toggleRole(res.GroupID, this.chatAdmin, 47);
    await this.toggleRole(res.GroupID, this.contentAuthor, 42);
    await this.toggleRole(res.GroupID, this.userManager, 43);
    await this.toggleRole(res.GroupID, this.contentManager, 36);
    await this.toggleRole(res.GroupID, this.backendUserAdmin, 58);
    const oldItems = this.ghc ? this.ghc.filter(item => !this.arrayToMap(this.assignedContentData)[item.dynamicContentEntrance_Id]) : [];
    const newItems = this.assignedContentData ? this.assignedContentData.filter(item => !this.arrayToMap(this.ghc)[item.dynamicContentEntrance_Id]) : [];
    for (const oldItem of oldItems) {
      await this.ghcService.remove(this.existingAssignedItemsConnections.find(el => el.dynamicContentEntrance_Id === oldItem.dynamicContentEntrance_Id).GroupHasContentID.toString());
    }
    for (const newItem of newItems) {
      await this.ghcService.create({ dynamicContentEntrance_Id: newItem.dynamicContentEntrance_Id, Groups_GroupID: this.itemId || res.GroupID, type: 'write' });
    }

    for (const element of this.groupData.filter(el => !el.ProfileID)) {
      const profile = new Models.Profile();
      profile.populateFromJSON(element);
      profile.userType = 'ibexfusion';
      profile.isAnonymous = false;
      const newProfile = await this.profileService.create(profile);
      element.ProfileID = newProfile.ProfileID as any;
    }

    const oldProfileItems = this.ghp ? this.ghp.filter(item => !this.arrayToMap(this.groupData, 'ProfileID')[item.ProfileID]) : [];
    const newProfileItems = this.groupData ? this.groupData.filter(item => !this.arrayToMap(this.ghp, 'ProfileID')[item.ProfileID]) : [];

    for (const newProfile of newProfileItems) {
      await this.ghpService.create({ GroupID: this.itemId || res.GroupID, ProfileID: newProfile.ProfileID });
    }

    for (const oldProfile of oldProfileItems) {
      await this.ghpService.remove(oldProfile.ID as any);
    }
    if (!this.itemId) {
      return this.router.navigate([this.router.url.split('/')[1], this.organisationId, 'groups', res.GroupID]);
    }
    this.ngOnInit();
  }

  arrayToMap(array, idField = 'packageInstalledAppId') {
    if (!array) {
      return [];
    }
    return array.reduce((map, item) => {
      map[item[idField]] = item;
      return map;
    }, {});
  }

  async abort() {
    if (!!this.handleBeforeUnload()) {
      this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
    } else {
      const confirmation = await this.dialogService.confirm(this.systemMessages['general']['pendingChanges']);
      if (confirmation) {
        this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
      }
    }
  }

  async toggleRole(groupId, activate, roleId) {
    const existing = !!this.ghrio ? this.ghrio.find(el => el.RoleID.toString() === roleId.toString()) : null;
    if (activate && !existing) {
      await this.ghrioService.create({ GroupID: groupId, RoleID: roleId, OrganisationID: this.organisationId });
    }
    if (!activate && !!existing) {
      await this.ghrioService.remove(existing.ID.toString());
    }
  }

  async addAssignedItem() {
    const items = JSON.parse(JSON.stringify(this.assignedContentData));
    const availableItems = this.packageInstalledAppsDynamicContent.filter(el => !items.find(sub => sub.dynamicContentEntrance_Id === el.dynamicContentEntranceId));
    availableItems.forEach(el => {
      let title = '';
      if (el && el.translations && el.translations[0] && el.translations[0].appTitle) {
        title = el.translations[0].appTitle;
      }
      el['title'] = title;
    });
    const dialogRef = this.dialog.open(AddItemsFromList, {
      width: '1200px', data: { availableItems: availableItems, idField: '_id', columns: [{ prop: 'title' }, { prop: '_id' }] }
    });
    const result = await dialogRef.afterClosed().toPromise();
    this.assignedContentData = null;
    if (result && result.additionalItems) {
      result.additionalItems.forEach(el => {
        const assignedApp = this.packageInstalledAppsDynamicContent.find(app => app._id === el);
        let title = '';
        if (assignedApp && assignedApp.translations && assignedApp.translations[0] && assignedApp.translations[0].appTitle) {
          title = assignedApp.translations[0].appTitle;
        }
        items.push({ dynamicContentEntrance_Id: assignedApp.dynamicContentEntranceId, title });
      });
    }
    setTimeout(() => {
      this.assignedContentData = items;
    }, 0);
  }

  async removeAssignedItem(event: any) {
    const items = JSON.parse(JSON.stringify(this.assignedContentData));
    this.assignedContentData = null;
    items.splice(items.findIndex(el => el.dynamicContentEntrance_Id === event.row.dynamicContentEntrance_Id), 1);
    setTimeout(() => {
      this.assignedContentData = items;
    }, 0);
  }

  async addProfile() {
    const ghp = JSON.parse(JSON.stringify(this.groupData));
    const params = { isAnonymous: false, ProfileID: { $nin: this.groupData.map(el => el.ProfileID) } };
    const dialogRef = this.dialog.open(AddItemsFromList, {
      width: '1200px', data: { params, service: this.profileService, idField: 'ProfileID', columns: [{ prop: 'email' }, { prop: 'firstName' }, { prop: 'lastName' }] }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.additionalItems?.length) {
        this.groupData = null;
        this.profileService.find(0, result.additionalItems.length, { ProfileID: { $in: result.additionalItems }, }).then(profileRes => {
          for (const profile of profileRes.data) {
            profile['GroupID'] = this.itemId;
            ghp.push(profile as any);
          }
          this.groupData = ghp;
        });
      }
    });
  }

  async addNewProfile() {
    const ghp = JSON.parse(JSON.stringify(this.groupData));
    const dialogRef = this.dialog.open(AddNewProfile, { width: '600px' });
    const result = await dialogRef.afterClosed().toPromise();
    if (result?.profile) {
      this.groupData = null;
      result.profile['GroupID'] = this.itemId;
      ghp.push(result.profile);
      setTimeout(() => {
        this.groupData = ghp;
      }, 0);
    }
  }

  async removeProfile(event: any) {
    const items = JSON.parse(JSON.stringify(this.groupData.filter(el => el.email !== event.row.email)));
    setTimeout(() => {
      this.groupData = items;
    }, 0);
  }

  userManagerChanged(event: any) {
    setTimeout(() => {
      if (!this.userManager) {
        ['orderAdmin', 'ticketingAdmin', 'securedContentAdmin', 'chatAdmin'].forEach(key => {
          this[key] = false;
        });
      }
    }, 100);
  }
}
